import React, { useState, useMemo } from 'react';
import { Pencil, X } from 'lucide-react';
import DOMPurify from 'dompurify';
import { useAuth } from '../context/AuthContext';
import { useContent } from '../context/ContentContext';
import { useToast } from '../hooks/use-toast';
import RichTextEditor from './RichTextEditor';

/**
 * Renders a block of admin-editable rich text (HTML). Backward-compatible
 * with existing plain-text values — those are auto-wrapped as paragraphs.
 *
 * When the current user is admin, hovering the block reveals a pencil that
 * opens a rich-text edit dialog with bold/italic/underline/list/indent tools.
 *
 * Props:
 *   contentKey      unique key for the block (stored in MongoDB)
 *   defaultValue    fallback when no admin has customized it yet
 *   paragraphClass  tailwind classes applied to the rendered container
 *   as              'p' (default) — kept for API compatibility; ignored now.
 *   label           friendly label shown in the edit dialog
 *   rows            editor minimum rows (default 8)
 *   align           'left'|'center' controls edit button placement
 */
const isLikelyHtml = (v) => typeof v === 'string' && /<[a-z][^>]*>/i.test(v);

// Escape HTML special chars for legacy plain-text content.
const esc = (s) => String(s)
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

// Turn bare URLs and emails in a plain-text string into anchor tags.
const URL_RE = /((?:https?:\/\/|www\.)[^\s<>"']+)/g;
const EMAIL_RE = /([\w.+-]+@[\w-]+(?:\.[\w-]+)+)/g;
const autolink = (text) => {
  // Order matters — do URLs first so we don't double-match email domains.
  return text
    .replace(URL_RE, (m) => {
      const href = m.startsWith('http') ? m : `https://${m}`;
      return `<a href="${href}" target="_blank" rel="noopener noreferrer">${m}</a>`;
    })
    .replace(EMAIL_RE, (m) => `<a href="mailto:${m}">${m}</a>`);
};

// Convert a legacy plain-text value (paragraphs separated by blank lines,
// with single newlines inside a paragraph) into safe HTML.
const legacyPlainToHtml = (value) => {
  const parts = String(value).split(/\n\s*\n/).map(s => s.trim()).filter(Boolean);
  if (parts.length === 0) return '';
  return parts
    .map(p => `<p>${autolink(esc(p)).replace(/\n/g, '<br>')}</p>`)
    .join('');
};

const toRenderableHtml = (value) => {
  if (!value) return '';
  const html = isLikelyHtml(value) ? String(value) : legacyPlainToHtml(value);
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [
      'p', 'br', 'strong', 'b', 'em', 'i', 'u', 's',
      'ul', 'ol', 'li', 'a', 'span',
    ],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'class', 'data-indent'],
    ALLOWED_URI_REGEXP: /^(?:https?:|mailto:|tel:|\/)/i,
    ADD_ATTR: ['target'],
  });
};

const EditableText = ({
  contentKey,
  defaultValue = '',
  paragraphClass = '',
  // eslint-disable-next-line no-unused-vars
  as = 'p',
  label,
  rows = 8,
  align = 'left',
  className = '',
}) => {
  const { isAdmin } = useAuth();
  const { getContent, saveContent } = useContent();
  const { toast } = useToast();
  const [editing, setEditing] = useState(false);
  const value = getContent(contentKey, defaultValue);

  const html = useMemo(() => toRenderableHtml(value), [value]);

  const editButton = isAdmin ? (
    <button
      type="button"
      onClick={() => setEditing(true)}
      data-testid={`edit-${contentKey}-button`}
      title={label ? `Edit: ${label}` : 'Edit content'}
      className={`absolute -top-2 ${align === 'center' ? 'right-2' : '-right-8 md:-right-10'} opacity-0 group-hover:opacity-100 focus:opacity-100 focus-within:opacity-100 inline-flex items-center justify-center w-7 h-7 border border-[#3a2359] bg-white text-[#3a2359] hover:bg-[#3a2359] hover:text-white z-10`}
    >
      <Pencil size={12} />
    </button>
  ) : null;

  return (
    <>
      <div className={`relative ${isAdmin ? 'group' : ''} ${className}`}>
        <div
          className={`editable-content ${paragraphClass}`}
          dangerouslySetInnerHTML={{ __html: html }}
        />
        {editButton}
      </div>
      {editing && (
        <EditContentDialog
          label={label || contentKey}
          initialValue={value}
          rows={rows}
          onClose={() => setEditing(false)}
          onSave={async (newVal) => {
            await saveContent(contentKey, newVal);
            toast({ title: 'Content updated' });
          }}
        />
      )}
    </>
  );
};

const EditContentDialog = ({ label, initialValue, rows, onClose, onSave }) => {
  // Convert legacy plain-text values to HTML so the rich editor can display them.
  const [value, setValue] = useState(() =>
    isLikelyHtml(initialValue) ? initialValue : legacyPlainToHtml(initialValue || '')
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await onSave(value);
      onClose();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white border border-[#e9e0cc] w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#e9e0cc]">
          <div>
            <p className="text-xs tracking-[0.2em] uppercase text-[#a68a5b]">Edit Content</p>
            <h3 className="font-display text-2xl text-[#2a1a45] leading-tight">{label}</h3>
          </div>
          <button onClick={onClose} className="text-[#5a534a] hover:text-[#3a2359]"><X size={20} /></button>
        </div>
        <form onSubmit={handleSave} className="px-6 py-5 space-y-4">
          {error && <div className="text-sm text-[#7a1c1c] bg-[#fdecec] border border-[#f0c4c4] p-3">{error}</div>}
          <p className="text-xs text-[#78716c]">Use the toolbar to bold, italicise, underline, or add bullets and numbered lists. Press <kbd>Tab</kbd> to indent and <kbd>Shift+Tab</kbd> to outdent — works on paragraphs and inside lists.</p>
          <RichTextEditor value={value} onChange={setValue} minRows={rows} />
          <div className="flex items-center justify-end gap-2 pt-3 border-t border-[#e9e0cc]">
            <button type="button" data-testid="edit-dialog-cancel-button" onClick={onClose} className="px-4 py-2 border border-[#e9e0cc] text-sm text-[#5a534a] hover:bg-[#faf7f0]">Cancel</button>
            <button type="submit" data-testid="edit-dialog-save-button" disabled={saving} className="px-5 py-2 bg-[#3a2359] text-white text-sm hover:bg-[#4d2f75] disabled:opacity-60">
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditableText;
